// src/components/ProtectedRoute.js
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Whitelist: every protected route MUST be listed here with its allowed roles.
// Anything not listed is denied by default. Path matching uses prefix.
// Order matters — first match wins, so put more specific paths above broader ones.
const routePermissions = [
  // Admin-only sections
  { prefix: '/dashboard',                 roles: ['admin'] },
  { prefix: '/announcement/insights',     roles: ['admin'] },

  // Admin + Office Staff
  { prefix: '/students',                  roles: ['admin', 'office_staff'] },
  { prefix: '/staffs',                  roles: ['admin', 'office_staff'] },
  { prefix: '/classes',                   roles: ['admin', 'office_staff'] },
  { prefix: '/exams',                     roles: ['admin', 'office_staff'] },
  { prefix: '/results',                   roles: ['admin', 'office_staff'] },
  { prefix: '/settings',                  roles: ['admin', 'office_staff'] },
  { prefix: '/academic/fees',             roles: ['admin', 'office_staff'] },
  { prefix: '/academic/transactions',     roles: ['admin', 'office_staff'] },
  { prefix: '/academic/transaction',      roles: ['admin', 'office_staff'] },
  { prefix: '/announcement/bulk-notice',  roles: ['admin', 'office_staff'] },
  { prefix: '/announcement/chats',        roles: ['admin', 'office_staff'] },
  { prefix: '/announcement/notification-tester', roles: ['admin', 'office_staff'] },
  { prefix: '/announcement/gallery',            roles: ['admin', 'office_staff'] },
  { prefix: '/vehicles/track',            roles: ['admin', 'office_staff'] },
  { prefix: '/utility/student-promotion', roles: ['admin', 'office_staff'] },
  { prefix: '/utility/system-properties', roles: ['admin'] },
  { prefix: '/utility/reconciliation',   roles: ['admin'] },

  // Mixed-role sections
  { prefix: '/attendance',                roles: ['admin', 'teacher', 'office_staff'] }
];

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // Show loading if auth is still initializing
  if (loading) {
    return <div>Loading...</div>;
  }

  // If no user, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for role to load before deciding (avoids false redirects)
  if (user.role === undefined || user.role === null) {
    return <div>Loading...</div>;
  }

  // Students can only access /home and /privacy-policy (public routes, no ProtectedRoute wrapper)
  // If a student somehow reaches a ProtectedRoute, redirect to /home
  if (user.role === 'student') {
    return <Navigate to="/home" replace />;
  }

  // Find matching rule (default-deny if not found)
  const rule = routePermissions.find(r => currentPath.startsWith(r.prefix));
  if (!rule) {
    console.warn(`Access denied: route "${currentPath}" is not in the permissions whitelist.`);
    return <Navigate to="/home" replace />;
  }

  if (!rule.roles.includes(user.role)) {
    return <Navigate to="/home" replace />;
  }

  return children;
}